import { invoicedToDate, isManualAdjustment, normalizeInvoiceRole, type InvoiceRole } from '../../modules/docs/utils/invoiceLedger';

export type ReconciliationInvoice = {
  id: string;
  role?: string | null;
  status?: string | null;
  grossTotal?: number | null;
  invoiceRef?: string | null;
  /** Set on invoices raised from the settled-contract delta; empty when raised by hand. */
  sourceSurveyEvent?: string | null;
};

export type ReconciliationPart = {
  id: string;
  label: string;
  ref: string | null;
  /** Signed: credits are negative. */
  amount: number;
};

export type InvoiceReconciliation = {
  /** The accepted quote (Final Survey once settled). */
  quoteTotal: number;
  variations: number;
  credits: number;
  /** Agreed reductions carried on a reissued balance. */
  deductions: number;
  /** Quote + variations − credits − deductions: what the customer has agreed to pay in total. */
  agreedTotal: number;
  invoiced: number;
  /** Agreed − invoiced; negative when over-invoiced. */
  remaining: number;
  parts: ReconciliationPart[];
  /**
   * complete  invoices add up to the agreed total
   * open      still to invoice, as expected before the balance
   * short     the balance is issued but invoices fall short of the agreed total
   * over      invoiced more than agreed
   */
  state: 'complete' | 'open' | 'short' | 'over';
};

const ROLE_LABELS: Record<InvoiceRole, string> = {
  deposit: 'Deposit',
  works: 'Balance',
  variation: 'Variation',
  credit: 'Credit',
};

const money = (value: number) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

function isLive(invoice: ReconciliationInvoice): boolean {
  return String(invoice.status || '').trim().toLowerCase() !== 'void';
}

/** Ties a workspace's invoices back to the accepted quote. Voided invoices are ignored. */
export function reconcileInvoices(args: {
  quoteTotal: number;
  invoices: ReconciliationInvoice[];
  /** Deduction lines on live reissued balances (positive amounts). */
  deductions?: number;
}): InvoiceReconciliation {
  const live = args.invoices.filter(isLive);
  // Only hand-raised variations and credits change what was agreed; delta ones are in the quote
  const sumRole = (role: InvoiceRole) => money(live
    .filter((invoice) => normalizeInvoiceRole(invoice.role) === role && isManualAdjustment(invoice))
    .reduce((sum, invoice) => sum + Math.abs(money(Number(invoice.grossTotal) || 0)), 0));

  const quoteTotal = money(args.quoteTotal);
  const variations = sumRole('variation');
  const credits = sumRole('credit');
  const deductions = money(Math.abs(Number(args.deductions) || 0));
  const agreedTotal = money(quoteTotal + variations - credits - deductions);
  const invoiced = money(invoicedToDate(live));
  const remaining = money(agreedTotal - invoiced);
  const balanceIssued = live.some((invoice) => normalizeInvoiceRole(invoice.role) === 'works');

  const order: Record<string, number> = { deposit: 0, works: 1, variation: 2, credit: 3 };
  const parts = live
    .map((invoice) => {
      const role = normalizeInvoiceRole(invoice.role);
      const gross = money(Number(invoice.grossTotal) || 0);
      return {
        id: invoice.id,
        label: role ? ROLE_LABELS[role] : 'Invoice',
        ref: invoice.invoiceRef || null,
        amount: role === 'credit' ? -Math.abs(gross) : gross,
        rank: role ? order[role] : 4,
      };
    })
    .sort((a, b) => a.rank - b.rank)
    .map(({ rank, ...part }) => part);

  const state: InvoiceReconciliation['state'] = Math.abs(remaining) <= 0.01
    ? 'complete'
    : remaining < 0
      ? 'over'
      : balanceIssued ? 'short' : 'open';

  return { quoteTotal, variations, credits, deductions, agreedTotal, invoiced, remaining, parts, state };
}
